import { useState, useMemo } from 'react'
import { usePipelineByStage } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ReportFilters } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, BarChart3, DollarSign, TrendingUp, Target, Loader2 } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import {
  CHART_COLORS,
  CATEGORICAL,
  GRACKLE_COLORS,
  SEMANTIC,
  ROTATED_LABEL_CHART_MARGIN,
  formatChartCurrency,
  formatChartCount,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

// GRK-BRAND-4: stages follow the brand's blue progression (deepening toward
// close), with Won=Pistachio, Lost=Raspberry, Service=Slate (see brand-decisions).
const STAGE_COLORS: Record<string, string> = {
  New: CATEGORICAL.powder.solid,
  'Need Identified': CATEGORICAL.steel.solid,
  'Presentation Complete': CATEGORICAL.navy.solid,
  'Initial Quote Complete': CATEGORICAL.teal.solid,
  'Final Quote': CATEGORICAL.prussian.solid,
  'Verbal Received': CATEGORICAL.plum.solid,
  'Closed Won': GRACKLE_COLORS.pistachio,
  'Closed Lost': CATEGORICAL.raspberry.solid,
  'Service (Do not Use)': CATEGORICAL.slate.solid,
}

export default function PipelineReport() {
  const [filters, setFilters] = useState<ReportFilterValues>({})

  const { data, isLoading, error } = usePipelineByStage({
    owner_id: filters.owner_id,
    startDate: filters.startDate,
    endDate: filters.endDate,
  })

  // 'Service (Do not Use)' is a forecast-only placeholder (100% probability). Keep it in
  // the Weighted Value total, but exclude it from the charts/table and the analytic
  // aggregates (count, total value, avg deal size, avg probability).
  const SERVICE_STAGE = 'Service (Do not Use)'

  // Full per-stage rows (incl. Service) — used only for the weighted-value total.
  const allStages = useMemo(() => {
    if (!data) return []
    return data.map((item: any) => ({
      stage: item.stage_name,
      count: item.opportunity_count,
      totalValue: parseFloat(item.total_value || '0'),
      avgValue: parseFloat(item.avg_value || '0'),
      avgProbability: parseFloat(item.avg_probability || '0'),
      weightedValue: parseFloat(item.weighted_value || '0'),
    }))
  }, [data])

  // Visible rows for charts/table/analytics — Service excluded.
  const pipelineData = useMemo(
    () => allStages.filter((item) => item.stage !== SERVICE_STAGE),
    [allStages]
  )

  // Calculate summary metrics
  const summary = useMemo(() => {
    const totalWeightedValue = allStages.reduce((sum, item) => sum + item.weightedValue, 0)

    if (!pipelineData.length)
      return {
        totalValue: 0,
        totalCount: 0,
        avgDealSize: 0,
        avgProbability: 0,
        totalWeightedValue,
      }

    const totalValue = pipelineData.reduce((sum, item) => sum + item.totalValue, 0)
    const totalCount = pipelineData.reduce((sum, item) => sum + item.count, 0)
    const avgDealSize = totalCount > 0 ? totalValue / totalCount : 0
    const avgProbability =
      pipelineData.reduce((sum, item) => sum + item.avgProbability * item.count, 0) / totalCount

    return {
      totalValue,
      totalCount,
      avgDealSize,
      avgProbability,
      totalWeightedValue,
    }
  }, [pipelineData, allStages])

  const handleExportCSV = () => {
    if (!data) return

    const exportData = data
      .filter((item: any) => item.stage_name !== SERVICE_STAGE)
      .map((item: any) => ({
        Stage: item.stage_name,
        'Opportunity Count': item.opportunity_count,
        'Total Value (CAD)': formatCurrency(item.total_value, 'CAD'),
        'Average Value (CAD)': formatCurrency(item.avg_value, 'CAD'),
        'Average Probability': `${parseFloat(item.avg_probability || '0').toFixed(1)}%`,
        'Weighted Value (CAD)': formatCurrency(item.weighted_value, 'CAD'),
      }))

    exportToCSV({ data: exportData, filename: 'pipeline-report.csv' })
  }

  // Check if we have data but it's empty
  const hasEmptyResults = !isLoading && !error && data && (!data || data.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pipeline Report</h1>
          <p className="text-muted-foreground">Opportunity pipeline and conversion metrics</p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">Failed to load pipeline report data.</p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pipeline Report</h1>
          <p className="text-muted-foreground">Opportunity pipeline and conversion metrics</p>
        </div>
        <div className="flex items-center gap-2">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="all"
            datePresets={[
              'this_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data}
            aria-label="Export pipeline report to CSV"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            borderColor: CATEGORICAL.clay.solid,
            color: SEMANTIC.warning.text,
          }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No pipeline data available
              </p>
              <p className="text-sm" style={{ color: GRACKLE_COLORS.prussianBlue }}>
                There are no active opportunities in the system to display pipeline metrics.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading pipeline report data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Pipeline Value</CardTitle>
                <DollarSign
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary.totalValue, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  total opportunity value
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Opportunity Count</CardTitle>
                <BarChart3
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.navy.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {summary.totalCount.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  active opportunities
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Average Deal Size</CardTitle>
                <Target
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.plum.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary.avgDealSize, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  per opportunity
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Weighted Pipeline (incl. Won)</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  style={{ color: CATEGORICAL.clay.solid }}
                  aria-hidden="true"
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatCurrency(summary.totalWeightedValue, 'CAD')}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  probability adjusted
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Opportunity Count by Stage */}
            <Card>
              <CardHeader>
                <CardTitle>Opportunities by Stage</CardTitle>
                <CardDescription>Number of opportunities in each stage</CardDescription>
              </CardHeader>
              <CardContent>
                {pipelineData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No pipeline data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={pipelineData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="stage"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCount} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number) => value.toLocaleString()}
                      />
                      <Legend />
                      <Bar dataKey="count" name="Opportunities">
                        {pipelineData.map((entry, index) => (
                          <Cell
                            key={`count-${index}`}
                            fill={STAGE_COLORS[entry.stage] || COLORS[0]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            {/* Total Value by Stage */}
            <Card>
              <CardHeader>
                <CardTitle>Pipeline Value by Stage</CardTitle>
                <CardDescription>Total opportunity value in each stage</CardDescription>
              </CardHeader>
              <CardContent>
                {pipelineData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No pipeline data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={pipelineData} margin={ROTATED_LABEL_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="stage"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number) => formatCurrency(value, 'CAD')}
                      />
                      <Legend />
                      <Bar dataKey="totalValue" name="Total Value">
                        {pipelineData.map((entry, index) => (
                          <Cell
                            key={`value-${index}`}
                            fill={STAGE_COLORS[entry.stage] || COLORS[1]}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Weighted Pipeline Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Weighted Pipeline by Stage</CardTitle>
              <CardDescription>Probability-adjusted opportunity value</CardDescription>
            </CardHeader>
            <CardContent>
              {pipelineData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No pipeline data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={pipelineData} margin={ROTATED_LABEL_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="stage"
                      angle={-45}
                      textAnchor="end"
                      height={100}
                      tick={{ fontSize: 11 }}
                    />
                    <YAxis tickFormatter={formatChartCurrency} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={<ChartTooltipContent />}
                      formatter={(value: number) => formatCurrency(value, 'CAD')}
                    />
                    <Legend />
                    <Bar dataKey="weightedValue" name="Weighted Value">
                      {pipelineData.map((entry, index) => (
                        <Cell
                          key={`weighted-${index}`}
                          fill={STAGE_COLORS[entry.stage] || COLORS[2]}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Pipeline Breakdown by Stage</CardTitle>
              <CardDescription>Complete breakdown of pipeline metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {!data || data.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No pipeline metrics available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Pipeline breakdown metrics"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Stage</th>
                        <th className="text-right p-2 font-medium">Count</th>
                        <th className="text-right p-2 font-medium">Total Value (CAD)</th>
                        <th className="text-right p-2 font-medium">Avg Value (CAD)</th>
                        <th className="text-right p-2 font-medium">Avg Probability</th>
                        <th className="text-right p-2 font-medium">Weighted Value (CAD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data
                        .filter((item: any) => item.stage_name !== SERVICE_STAGE)
                        .map((item: any, index: number) => (
                          <tr key={index} className="border-b hover:bg-muted/50">
                            <td className="p-2">{item.stage_name}</td>
                            <td className="text-right p-2">{item.opportunity_count}</td>
                            <td className="text-right p-2">
                              {formatCurrency(item.total_value, 'CAD')}
                            </td>
                            <td className="text-right p-2">
                              {formatCurrency(item.avg_value, 'CAD')}
                            </td>
                            <td className="text-right p-2">
                              {parseFloat(item.avg_probability || '0').toFixed(1)}%
                            </td>
                            <td className="text-right p-2">
                              {formatCurrency(item.weighted_value, 'CAD')}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
